import { addUtmData } from './websiteTracking';

/** Hosts that are ArchiPro's own, so never outbound. */
const isArchiProHost = (host: string): boolean =>
    host === 'archipro.co.nz' ||
    host.endsWith('.archipro.co.nz') ||
    host === 'archipro.com.au' ||
    host.endsWith('.archipro.com.au');

/**
 * Destinations that break when tracking is appended. Mirrors the list the
 * outbound CTAs already honour in {@link websiteTracking}.
 */
const UTM_BLACKLIST = ['clivebarrington.com'];

const shouldTag = (href: string): boolean => {
    try {
        const url = new URL(href);
        if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
        const host = url.host.replace(/^www\./, '');
        return !isArchiProHost(url.host) && !UTM_BLACKLIST.includes(host);
    } catch {
        return false;
    }
};

/** `href="…"`, `href='…'` — CMS markup uses both. */
const HREF_ATTR = /href=("|')(https?:\/\/[^"']+)\1/gi;

/**
 * Tag the outbound links inside a block of CMS rich text with the site's UTM
 * parameters.
 *
 * A supplier's own CTAs — "Visit Website", "Visit site" — have always carried
 * `utm_source=archipro`, because they are built from the professional's
 * website field and pass through the outbound link helpers. Links the author
 * typed into a content block do not: they are raw anchors inside an HTML
 * string, so the same destination arrived untagged and the referral was
 * invisible in the client's analytics. On the Colorsteel product page the
 * "Visit Website" button and the "free samples" sentence pointed at the very
 * same URL, one tagged and one not.
 *
 * Rewritten as text rather than parsed: this runs on the server, where there
 * is no DOM, and the markup is stored as a string and handed to
 * `dangerouslySetInnerHTML` unchanged. Only the `href` value is touched, and
 * only when it parses as an outbound http(s) URL — anything else, including
 * relative links, mailto:, tel: and ArchiPro's own pages, is left alone.
 *
 * `__apid` is deliberately not added here: it identifies the reader, is only
 * known in the browser, and belongs on a click rather than baked into markup
 * that gets cached.
 */
export const addUtmToContentLinks = (html: string): string => {
    if (!html) return html;

    return html.replace(HREF_ATTR, (match, quote: string, href: string) => {
        if (!shouldTag(href)) return match;
        try {
            return `href=${quote}${addUtmData(new URL(href)).href}${quote}`;
        } catch {
            return match;
        }
    });
};
